import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Card,
  CardContent,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Person as PersonIcon,
  Pets as PetsIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Calendar, dayjsLocalizer, View } from 'react-big-calendar';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import 'dayjs/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import rendezVousService, { RendezVous } from '../../services/rendezvousService';

dayjs.extend(isoWeek);
dayjs.locale('fr');
const localizer = dayjsLocalizer(dayjs);

export interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: RendezVous;
}

const messages = {
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  allDay: 'Toute la journée',
  week: 'Semaine',
  work_week: 'Semaine de travail',
  day: 'Jour',
  month: 'Mois',
  previous: 'Précédent',
  next: 'Suivant',
  yesterday: 'Hier',
  tomorrow: 'Demain',
  today: "Aujourd'hui",
  agenda: 'Agenda',
  noEventsInRange: 'Aucun rendez-vous sur cette période.',
  showMore: (total: number) => `+ ${total} de plus`,
};

const PlanningVeterinaire: React.FC = () => {
  const [rendezVous, setRendezVous] = useState<RendezVous[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<View>('week');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    rdv: RendezVous | null;
    action: 'confirm' | 'cancel' | null;
  }>({ open: false, rdv: null, action: null });
  const [processing, setProcessing] = useState(false);

  const loadRendezVous = useCallback(async () => {
    setLoading(true);
    try {
      let dateDebut: string;
      let dateFin: string;

      if (viewMode === 'day') {
        dateDebut = dayjs(selectedDate).format('YYYY-MM-DD');
        dateFin = dateDebut;
      } else if (viewMode === 'month') {
        dateDebut = dayjs(selectedDate).startOf('month').format('YYYY-MM-DD');
        dateFin = dayjs(selectedDate).endOf('month').format('YYYY-MM-DD');
      } else {
        const startWeek = dayjs(selectedDate).startOf('isoWeek');
        dateDebut = startWeek.format('YYYY-MM-DD');
        dateFin = startWeek.add(6, 'day').format('YYYY-MM-DD');
      }

      const data = await rendezVousService.getPlanning({
        date_debut: dateDebut,
        date_fin: dateFin
      });

      setRendezVous(data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement du planning');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, viewMode]);

  useEffect(() => {
    loadRendezVous();
  }, [loadRendezVous]);

  const handleConfirmRendezVous = async () => {
    if (!confirmDialog.rdv) return;

    setProcessing(true);
    try {
      if (confirmDialog.action === 'confirm') {
        await rendezVousService.confirmerRendezVous(confirmDialog.rdv.id);
      } else if (confirmDialog.action === 'cancel') {
        await rendezVousService.annulerRendezVous(confirmDialog.rdv.id);
      }

      await loadRendezVous();
      setConfirmDialog({ open: false, rdv: null, action: null });
    } catch (err) {
      setError('Erreur lors de la mise à jour du rendez-vous');
      console.error('Erreur:', err);
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (dateString: string) => {
    return dayjs(dateString).format('HH:mm');
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('dddd D MMMM');
  };

  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'demande':
        return 'warning';
      case 'confirme':
        return 'success';
      case 'annule':
        return 'error';
      case 'termine':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'demande':
        return 'En attente';
      case 'confirme':
        return 'Confirmé';
      case 'annule':
        return 'Annulé';
      case 'termine':
        return 'Terminé';
      default:
        return statut;
    }
  };

  const canConfirm = (rdv: RendezVous) => rdv.statut === 'demande';
  const canCancel = (rdv: RendezVous) => ['demande', 'confirme'].includes(rdv.statut);

  const events: CalendarEvent[] = rendezVous
    .filter((rdv) => rdv.statut !== 'annule')
    .map((rdv) => {
      const start = dayjs(rdv.date_heure).toDate();
      const end = dayjs(rdv.date_heure).add(rdv.duree_minutes, 'minute').toDate();
      const title = `${formatTime(rdv.date_heure)} - ${rdv.proprietaire_nom || '?'} / ${rdv.animal_nom || '?'}`;
      return {
        id: rdv.id,
        title,
        start,
        end,
        resource: rdv
      };
    });

  const eventStyleGetter = (event: CalendarEvent) => {
    const color = getStatusColor(event.resource.statut);
    const colorMap: Record<string, string> = {
      warning: '#ed6c02',
      success: '#2e7d32',
      error: '#d32f2f',
      info: '#0288d1',
      default: '#757575'
    };
    return {
      style: {
        backgroundColor: colorMap[color] || colorMap.default,
        borderRadius: 4
      }
    };
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    setConfirmDialog({ open: true, rdv: event.resource, action: null });
  };

  const handleViewChange = (newView: View) => {
    setViewMode(newView);
  };

  const handleNavigate = (newDate: Date) => {
    setSelectedDate(newDate);
  };

  if (loading && rendezVous.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" color="primary">
          Mon planning
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && handleViewChange(v)}
            size="small"
          >
            <ToggleButton value="day">Jour</ToggleButton>
            <ToggleButton value="week">Semaine</ToggleButton>
            <ToggleButton value="month">Mois</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Actualiser">
            <IconButton onClick={loadRendezVous} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ minWidth: 120 }}>
          <CardContent>
            <Typography color="textSecondary" variant="body2">
              Total RDV
            </Typography>
            <Typography variant="h5">{rendezVous.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 120 }}>
          <CardContent>
            <Typography color="warning.main" variant="body2">
              En attente
            </Typography>
            <Typography variant="h5" color="warning.main">
              {rendezVous.filter((rdv) => rdv.statut === 'demande').length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 120 }}>
          <CardContent>
            <Typography color="success.main" variant="body2">
              Confirmés
            </Typography>
            <Typography variant="h5" color="success.main">
              {rendezVous.filter((rdv) => rdv.statut === 'confirme').length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ height: 600, minHeight: 400 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          view={viewMode}
          onView={handleViewChange}
          date={selectedDate}
          onNavigate={handleNavigate}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          messages={messages}
          culture="fr"
        />
      </Box>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, rdv: null, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.rdv
            ? `Rendez-vous - ${confirmDialog.rdv.proprietaire_nom} / ${confirmDialog.rdv.animal_nom}`
            : 'Détail du rendez-vous'}
        </DialogTitle>
        <DialogContent>
          {confirmDialog.rdv && (
            <Box>
              <Stack direction="row" spacing={1} mb={2}>
                <Chip
                  label={getStatusLabel(confirmDialog.rdv.statut)}
                  color={getStatusColor(confirmDialog.rdv.statut) as any}
                  size="small"
                />
              </Stack>
              <Typography variant="body2" gutterBottom>
                <strong>Date :</strong> {formatDate(confirmDialog.rdv.date_heure)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Heure :</strong> {formatTime(confirmDialog.rdv.date_heure)} ({confirmDialog.rdv.duree_minutes} min)
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon fontSize="small" color="primary" />
                <Typography variant="body2">{confirmDialog.rdv.proprietaire_nom}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PetsIcon fontSize="small" color="primary" />
                <Typography variant="body2">{confirmDialog.rdv.animal_nom}</Typography>
              </Box>
              <Typography variant="body2" gutterBottom>
                <strong>Motif :</strong> {confirmDialog.rdv.motif}
              </Typography>
              {confirmDialog.rdv.notes_proprietaire && (
                <Typography variant="body2" color="text.secondary">
                  {confirmDialog.rdv.notes_proprietaire}
                </Typography>
              )}
              {!confirmDialog.action && (
                <Box display="flex" gap={1} mt={2}>
                  {canConfirm(confirmDialog.rdv) && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckIcon />}
                      onClick={() =>
                        setConfirmDialog((prev) => ({ ...prev, action: 'confirm' }))
                      }
                    >
                      Confirmer
                    </Button>
                  )}
                  {canCancel(confirmDialog.rdv) && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      onClick={() =>
                        setConfirmDialog((prev) => ({ ...prev, action: 'cancel' }))
                      }
                    >
                      Annuler le RDV
                    </Button>
                  )}
                </Box>
              )}
              {confirmDialog.action && (
                <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                  <Typography gutterBottom>
                    {confirmDialog.action === 'confirm'
                      ? 'Êtes-vous sûr de vouloir confirmer ce rendez-vous ?'
                      : 'Êtes-vous sûr de vouloir annuler ce rendez-vous ?'}
                  </Typography>
                  <DialogActions sx={{ px: 0 }}>
                    <Button
                      onClick={() =>
                        setConfirmDialog((prev) => ({ ...prev, action: null }))
                      }
                      disabled={processing}
                    >
                      Retour
                    </Button>
                    <Button
                      onClick={handleConfirmRendezVous}
                      color={confirmDialog.action === 'confirm' ? 'success' : 'error'}
                      variant="contained"
                      disabled={processing}
                    >
                      {processing ? (
                        <CircularProgress size={20} />
                      ) : confirmDialog.action === 'confirm' ? (
                        'Confirmer'
                      ) : (
                        'Annuler le RDV'
                      )}
                    </Button>
                  </DialogActions>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        {!confirmDialog.action && (
          <DialogActions>
            <Button onClick={() => setConfirmDialog({ open: false, rdv: null, action: null })}>
              Fermer
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default PlanningVeterinaire;
